import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";

import type { Database } from "@/shared/types/database.types";

type Tables = Database["public"]["Tables"];

export type EstateRow = Tables["estates"]["Row"];

export type EstateWill = Tables["estate_wills"]["Row"];
export type EstateTrust = Tables["estate_trusts"]["Row"];
export type MarriageHistoryRow = Tables["estate_marriage_history"]["Row"];
export type EstateCase = Tables["cases"]["Row"] & {
  courts: Pick<Tables["courts"]["Row"], "name"> | null;
};

export interface EstateSummaryRecords {
  will: EstateWill | null;
  trusts: EstateTrust[];
  primaryCase: EstateCase | null;
  marriageHistory: MarriageHistoryRow[];
  propertyStates: string[];
}

export async function fetchEstateJsonFields(
  estateId: number,
): Promise<Pick<EstateRow, "estate_metadata" | "marriage_info">> {
  const { data, error } = await supabase
    .from("estates")
    .select("estate_metadata, marriage_info")
    .eq("estate_id", estateId)
    .single();
  if (error) throw error;
  return data;
}

export const estateSummaryRecordsKey = (estateId: number | undefined) =>
  ["estateSummaryRecords", estateId] as const;

const firstRow = <T>(joined: T | T[] | null | undefined): T | null =>
  (Array.isArray(joined) ? joined[0] : joined) ?? null;

async function fetchEstateSummaryRecords(estateId: number): Promise<EstateSummaryRecords> {
  const [will, trusts, cases, marriageHistory, propertySitus] = await Promise.all([
    supabase.from("estate_wills").select("*").eq("estate_id", estateId).maybeSingle(),
    supabase.from("estate_trusts").select("*").eq("estate_id", estateId).order("id"),
    supabase.from("cases").select("*, courts(name)").eq("estate_id", estateId).order("id"),
    supabase.from("estate_marriage_history").select("*").eq("estate_id", estateId).order("id"),
    supabase
      .from("entity_addresses")
      .select("addresses(state), estate_items!inner(estate_id, category)")
      .eq("estate_items.estate_id", estateId)
      .eq("estate_items.category", "Properties")
      .eq("purpose", "situs")
      .is("valid_to", null),
  ]);

  for (const result of [will, trusts, cases, marriageHistory, propertySitus]) {
    if (result.error) throw result.error;
  }

  const allCases = (cases.data ?? []) as EstateCase[];

  return {
    will: will.data,
    trusts: trusts.data ?? [],
    primaryCase: allCases.find((c) => c.case_type !== "ancillary") ?? allCases[0] ?? null,
    marriageHistory: marriageHistory.data ?? [],
    propertyStates: (propertySitus.data ?? []).flatMap((link) => {
      const state = firstRow<{ state: string | null }>(link.addresses)?.state;
      return state ? [state] : [];
    }),
  };
}

export function useEstateSummaryRecords(estateId: number | undefined) {
  return useQuery({
    queryKey: estateSummaryRecordsKey(estateId),
    queryFn: () => fetchEstateSummaryRecords(estateId!),
    enabled: !!estateId,
  });
}
